/**
 * WebSocket 클라이언트 생성
 *
 * mainServer 및 quoteServer용 WebSocket 연결 관리
 */

import { createClient } from "graphql-ws";

import { ENDPOINTS } from './config';
import { STORAGE_KEYS } from './storage';
import { classifyAuthError, isAuthError, handleAuthError } from './errorUtils';

/**
 * mainServer용 WebSocket 클라이언트 생성
 */
export function createWsClient() {
  let authErrorOccurred = false;

  return createClient({
    url: ENDPOINTS.subscriptions,
    lazy: false,
    retryAttempts: 3,
    shouldRetry: (errOrCloseEvent) => {
      if (authErrorOccurred) {
        console.log("[WebSocket] 인증 에러로 인해 재시도 중단");
        return false;
      }

      // ============================================================
      // [임시] 토큰 인증 비활성화 - 인증 에러 체크 건너뜀
      // ============================================================
      /*
      if (isAuthError(errOrCloseEvent)) {
        const errorType = classifyAuthError(errOrCloseEvent);
        console.log("[WebSocket] 인증 에러 감지 - 재시도 중단:", { errorType });
        authErrorOccurred = true;
        setTimeout(() => handleAuthError('shouldRetry', errorType), 0);
        return false;
      }
      */

      console.log("[WebSocket] 일시적 에러 - 재시도 진행");
      return true;
    },
    retryWait: (retries) => Math.min(1000 * Math.pow(2, retries), 30000),
    keepAlive: 10000,
    connectionParams: () => {
      // ============================================================
      // [임시] 토큰 인증 비활성화 - 빈 파라미터 반환
      // ============================================================
      console.log("[WebSocket] 연결 (인증 비활성화됨):", {
        timestamp: new Date().toISOString()
      });

      return {
        // 토큰 없이 연결
        deviceId: localStorage.getItem(STORAGE_KEYS.DEVICE_ID) || ""
      };

      /* [주석처리됨] 토큰 인증 로직
      const token = localStorage.getItem(STORAGE_KEYS.ACCESS_TOKEN);
      const deviceId = localStorage.getItem(STORAGE_KEYS.DEVICE_ID) || "";

      console.log("[WebSocket] 연결 파라미터 설정:", {
        hasToken: !!token,
        hasDeviceId: !!deviceId,
        timestamp: new Date().toISOString()
      });

      return {
        authorization: token ? `Bearer ${token}` : "",
        deviceId: deviceId
      };
      */
    },
    on: {
      connected: () => {
        authErrorOccurred = false;
        console.log("[WebSocket] 연결 성공:", {
          url: ENDPOINTS.subscriptions,
          timestamp: new Date().toISOString()
        });
      },
      error: (err) => {
        const errorType = classifyAuthError(err);
        console.error("[WebSocket] 에러:", {
          error: err,
          message: err?.message,
          classifiedType: errorType,
          timestamp: new Date().toISOString()
        });

        // ============================================================
        // [임시] 토큰 인증 비활성화 - 인증 에러 처리 건너뜀
        // ============================================================
        /*
        if (isAuthError(err) && !authErrorOccurred) {
          authErrorOccurred = true;
          handleAuthError('error', errorType);
        }
        */
      },
      closed: (event) => {
        console.log("[WebSocket] 연결 종료:", {
          code: event?.code,
          reason: event?.reason,
          wasClean: event?.wasClean,
          timestamp: new Date().toISOString()
        });

        // ============================================================
        // [임시] 토큰 인증 비활성화 - 인증 에러 처리 건너뜀
        // ============================================================
        /*
        if (!event?.wasClean && isAuthError(event) && !authErrorOccurred) {
          authErrorOccurred = true;
          handleAuthError('closed', classifyAuthError(event));
        }
        */
      },
    },
  });
}

/**
 * quoteServer용 WebSocket 클라이언트 생성 (시세 데이터 전용)
 */
export function createQuoteWsClient() {
  let authErrorOccurred = false;

  return createClient({
    url: ENDPOINTS.quoteGraphql,
    lazy: false,
    retryAttempts: 3,
    shouldRetry: (errOrCloseEvent) => {
      if (authErrorOccurred) {
        console.log("[Quote WebSocket] 인증 에러로 인해 재시도 중단");
        return false;
      }

      // ============================================================
      // [임시] 토큰 인증 비활성화
      // ============================================================
      console.log("[Quote WebSocket] 일시적 에러 - 재시도 진행");
      return true;
    },
    retryWait: (retries) => Math.min(1000 * Math.pow(2, retries), 30000),
    keepAlive: 10000,
    connectionParams: () => {
      // ============================================================
      // [임시] 토큰 인증 비활성화 - 빈 파라미터 반환
      // ============================================================
      console.log("[Quote WebSocket] 연결 (인증 비활성화됨):", {
        timestamp: new Date().toISOString()
      });

      return {
        deviceId: localStorage.getItem(STORAGE_KEYS.DEVICE_ID) || ""
      };

      /* [주석처리됨] 토큰 인증 로직
      const token = localStorage.getItem(STORAGE_KEYS.ACCESS_TOKEN);
      const deviceId = localStorage.getItem(STORAGE_KEYS.DEVICE_ID) || "";

      console.log("[Quote WebSocket] 연결 파라미터 설정:", {
        hasToken: !!token,
        hasDeviceId: !!deviceId,
        timestamp: new Date().toISOString()
      });

      return {
        authorization: token ? `Bearer ${token}` : "",
        deviceId: deviceId
      };
      */
    },
    on: {
      connected: () => {
        authErrorOccurred = false;
        console.log("[Quote WebSocket] 연결 성공:", {
          url: ENDPOINTS.quoteGraphql,
          timestamp: new Date().toISOString()
        });
      },
      error: (err) => {
        const errorType = classifyAuthError(err);
        console.error("[Quote WebSocket] 에러:", {
          error: err,
          message: err?.message,
          classifiedType: errorType,
          timestamp: new Date().toISOString()
        });
      },
      closed: (event) => {
        console.log("[Quote WebSocket] 연결 종료:", {
          code: event?.code,
          reason: event?.reason,
          wasClean: event?.wasClean,
          timestamp: new Date().toISOString()
        });
      },
    },
  });
}
